import { UserRole } from '../enums'
import { AppRoute } from './app-routes'

/**
 * Route permission configuration
 * Maps routes to required roles
 */
export interface RoutePermissionConfig {
  route: string | RegExp
  allowedRoles: UserRole[]
}

/**
 * Route permissions mapping
 */
const ROUTE_PERMISSIONS: RoutePermissionConfig[] = [
  // Admin routes
  {
    route: AppRoute.SYSTEM_ADMIN_DASHBOARD_ROUTE,
    allowedRoles: [UserRole.SYSTEM_ADMIN]
  },
  {
    route: AppRoute.SYSTEM_ADMIN_SETTINGS_ROUTE,
    allowedRoles: [UserRole.SYSTEM_ADMIN]
  },
  
  // Monitor routes
  {
    route: AppRoute.MONITOR_DASHBOARD_ROUTE,
    allowedRoles: [UserRole.MONITOR]
  },
  {
    route: AppRoute.MONITOR_SETTINGS_ROUTE,
    allowedRoles: [UserRole.MONITOR]
  },
  
  // Business Owner routes
  {
    route: AppRoute.BUSINESS_OWNER_DASHBOARD_ROUTE,
    allowedRoles: [UserRole.BUSINESS_OWNER]
  },
  {
    route: AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE,
    allowedRoles: [UserRole.BUSINESS_OWNER, UserRole.SYSTEM_ADMIN]
  },
  {
    route: AppRoute.BUSINESS_OWNER_MENU_ROUTE,
    allowedRoles: [UserRole.BUSINESS_OWNER, UserRole.SYSTEM_ADMIN]
  },
  {
    route: AppRoute.BUSINESS_OWNER_SETTINGS_ROUTE,
    allowedRoles: [UserRole.BUSINESS_OWNER, UserRole.SYSTEM_ADMIN]
  },
  {
    route: AppRoute.BUSINESS_OWNER_USERS_ROUTE,
    allowedRoles: [UserRole.BUSINESS_OWNER, UserRole.SYSTEM_ADMIN]
  },
  
  // Business Employee routes
  {
    route: AppRoute.BUSINESS_EMPLOYEE_DASHBOARD_ROUTE,
    allowedRoles: [UserRole.BUSINESS_EMPLOYEE]
  },
  {
    route: AppRoute.BUSINESS_EMPLOYEE_MENU_ROUTE,
    allowedRoles: [UserRole.BUSINESS_EMPLOYEE]
  },
  {
    route: AppRoute.BUSINESS_EMPLOYEE_SETTINGS_ROUTE,
    allowedRoles: [UserRole.BUSINESS_EMPLOYEE]
  },
  
  // Business Cashier routes
  {
    route: AppRoute.BUSINESS_CASHIER_DASHBOARD_ROUTE,
    allowedRoles: [UserRole.BUSINESS_CASHIER]
  },
  {
    route: AppRoute.BUSINESS_CASHIER_MENU_ROUTE,
    allowedRoles: [UserRole.BUSINESS_CASHIER]
  },
  {
    route: AppRoute.BUSINESS_CASHIER_SETTINGS_ROUTE,
    allowedRoles: [UserRole.BUSINESS_CASHIER]
  },
  
  // Public routes (accessible by all)
  {
    route: AppRoute.HOME_ROUTE,
    allowedRoles: [UserRole.SYSTEM_ADMIN, UserRole.MONITOR, UserRole.BUSINESS_OWNER, UserRole.BUSINESS_EMPLOYEE, UserRole.CLIENT]
  }
]

/**
 * Check if a route matches a pattern (string or RegExp)
 */
function matchesRoute(route: string, pattern: string | RegExp): boolean {
  if (pattern instanceof RegExp) {
    return pattern.test(route)
  }
  return route.startsWith(pattern)
}

/**
 * Check if user role can access a specific route
 * @param route - The route path to check
 * @param userRole - The user's role
 * @returns true if user can access the route
 */
export function canAccessRoute(
  route: string, 
  userRole: UserRole
): boolean {
  // Find matching route configuration
  const routeConfig = ROUTE_PERMISSIONS.find(config => matchesRoute(route, config.route))
  
  // If no config found, allow access (public route by default)
  if (!routeConfig) {
    return true
  }
  
  // Check if user role is allowed
  return routeConfig.allowedRoles.includes(userRole)
}

/**
 * Get default redirect path for a user role
 */
export function getDefaultRouteForRole(userRole: UserRole): string {
  switch (userRole) {
    case UserRole.SYSTEM_ADMIN:
      return AppRoute.SYSTEM_ADMIN_DASHBOARD_ROUTE
    case UserRole.MONITOR:
      return AppRoute.MONITOR_DASHBOARD_ROUTE
    case UserRole.BUSINESS_OWNER:
      return AppRoute.BUSINESS_OWNER_DASHBOARD_ROUTE
    case UserRole.BUSINESS_EMPLOYEE:
      return AppRoute.BUSINESS_EMPLOYEE_DASHBOARD_ROUTE
    case UserRole.BUSINESS_CASHIER:
      return AppRoute.BUSINESS_CASHIER_DASHBOARD_ROUTE
    default:
      return AppRoute.HOME_ROUTE
  }
}

